import { useState, useRef, type ReactNode, type TouchEvent } from 'react';
import { useT } from '../../i18n/index.jsx';

type PullState = 'idle' | 'pulling' | 'ready' | 'refreshing';

interface PullToRefreshProps {
  children: ReactNode;
}

/**
 * PullToRefresh — Touch-basierter Pull-to-Refresh-Wrapper für Mobile.
 *
 * Erkennt 4 Zustände: idle → pulling → ready → refreshing → idle.
 * Reagiert nur, wenn `window.scrollY <= 0` (d.h. ganz oben).
 */
export function PullToRefresh({ children }: PullToRefreshProps) {
  const t = useT();
  const [state, setState] = useState<PullState>('idle');
  const startY = useRef<number>(0);

  const onTouchStart = (e: TouchEvent<HTMLDivElement>): void => {
    if (window.scrollY <= 0) startY.current = e.touches[0].clientY;
  };
  const onTouchMove = (e: TouchEvent<HTMLDivElement>): void => {
    if (!startY.current) return;
    const d = e.touches[0].clientY - startY.current;
    if (d > 60 && window.scrollY <= 0) setState('ready');
    else if (d > 10 && window.scrollY <= 0) setState('pulling');
  };
  const onTouchEnd = (): void => {
    if (state === 'ready') {
      setState('refreshing');
      setTimeout(function () {
        setState('idle');
        startY.current = 0;
      }, 800);
    } else {
      setState('idle');
      startY.current = 0;
    }
  };

  const msg =
    state === 'pulling'
      ? t('mobilePtrPull')
      : state === 'ready'
        ? t('mobilePtrRelease')
        : state === 'refreshing'
          ? t('mobilePtrRefreshing')
          : '';

  return (
    <div
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}>
      {state !== 'idle' &&
        <div
          className="ptr-indicator"
          style={{
            background: state === 'ready' ? 'var(--accent)' : state === 'refreshing' ? '#f59e0b' : 'var(--surface2)',
            color: state === 'ready' || state === 'refreshing' ? '#fff' : 'var(--muted)',
            opacity: state === 'pulling' ? 0.7 : 1,
          }}>
          {(state === 'refreshing' ? '' : '\u2193 ') + msg}
        </div>}
      {children}
    </div>
  );
}
